import { Configurations } from '../models/config.model';

export const NAMESPACE_NAVIGATION = 'navigation';

export enum NavigationConfigurationKeys {
  HomeAndBrowseEnabled = 'home-and-browse-enabled',
  OffboardingEnabled = 'is-offboarding-enabled',
  ReportingLinkEnabled = 'reporting-link-enabled',
  DropshipLinkUrl = 'dropship-link-url',
  PresetSwitchButton = 'preset-switch-button',
}

export interface DropshipLinkPlacementConfig {
  active: boolean;
  classes: string;
  tooltipEnabled: boolean;
}

export interface DropshipLinkUrlConfig {
  url: string;
  navigation: DropshipLinkPlacementConfig;
  userMenu: DropshipLinkPlacementConfig;
}

export const defaultDropshipLinkPlacementConfig: DropshipLinkPlacementConfig = {
  active: false,
  classes: '',
  tooltipEnabled: false,
};

export const defaultDropshipLinkUrlConfig: DropshipLinkUrlConfig = {
  url: '',
  navigation: { ...defaultDropshipLinkPlacementConfig },
  userMenu: { ...defaultDropshipLinkPlacementConfig },
};

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function parsePlacementConfig(
  raw: unknown,
  fallbackActive: boolean,
): DropshipLinkPlacementConfig {
  if (!isObject(raw)) {
    return {
      ...defaultDropshipLinkPlacementConfig,
      active: fallbackActive,
    };
  }

  return {
    active:
      typeof raw.active === 'boolean' ? raw.active : Boolean(fallbackActive),
    classes: typeof raw.classes === 'string' ? raw.classes : '',
    tooltipEnabled:
      typeof raw.tooltipEnabled === 'boolean' ? raw.tooltipEnabled : false,
  };
}

export function parseDropshipLinkUrlConfig(
  rawConfig: unknown,
): DropshipLinkUrlConfig {
  if (typeof rawConfig === 'string') {
    const trimmedConfig = rawConfig.trim();

    if (trimmedConfig.startsWith('{')) {
      try {
        return parseDropshipLinkUrlConfig(
          JSON.parse(trimmedConfig) as Record<string, unknown>,
        );
      } catch {
        return defaultDropshipLinkUrlConfig;
      }
    }

    const url = rawConfig;
    const isActive = Boolean(url);

    return {
      url,
      navigation: parsePlacementConfig(undefined, isActive),
      userMenu: parsePlacementConfig(undefined, isActive),
    };
  }

  if (!isObject(rawConfig)) {
    return defaultDropshipLinkUrlConfig;
  }

  const url = typeof rawConfig.url === 'string' ? rawConfig.url : '';
  const isActive = Boolean(url);

  return {
    url,
    navigation: parsePlacementConfig(rawConfig.navigation, isActive),
    userMenu: parsePlacementConfig(rawConfig.userMenu, isActive),
  };
}

/**
 * Appearance of the preset-switch button for one target preset. The button
 * always shows the *opposite* of the current preset, so each state describes
 * the preset it switches TO when clicked.
 */
export interface PresetSwitchStateConfig {
  preset: string;
  /**
   * Button label. Either plain text, or a `@@messageId` reference that is
   * resolved at runtime by the DynamicLocalePipe (e.g. `@@presetFutureDisplayName`)
   * so the label is translatable straight from the rule, like other panel configs.
   */
  label: string;
  icon: string;
  classes: string;
  /**
   * Optional inline CSS applied via [style], for colors that can't be expressed
   * with predefined classes, e.g. "background-color: rgb(232, 88, 88); color: white;".
   */
  styles: string;
}

export interface PresetSwitchButtonConfig {
  active: boolean;
  showOnMobile: boolean;
  showOnDesktop: boolean;
  routes: string[];
  tooltipEnabled: boolean;
  primary: PresetSwitchStateConfig;
  secondary: PresetSwitchStateConfig;
}

// Labels default to `@@messageId` references resolved at runtime by the
// DynamicLocalePipe, so the out-of-the-box button is translatable.
export const defaultPresetSwitchPrimaryState: PresetSwitchStateConfig = {
  preset: 'now',
  label: '@@presetNowDisplayName',
  icon: 'clock',
  classes: '',
  styles: '',
};

export const defaultPresetSwitchSecondaryState: PresetSwitchStateConfig = {
  preset: 'future',
  label: '@@presetFutureDisplayName',
  icon: 'calendar',
  classes: '',
  styles: '',
};

export const defaultPresetSwitchButtonConfig: PresetSwitchButtonConfig = {
  active: false,
  showOnMobile: true,
  showOnDesktop: false,
  routes: ['home'],
  tooltipEnabled: false,
  primary: { ...defaultPresetSwitchPrimaryState },
  secondary: { ...defaultPresetSwitchSecondaryState },
};

function parsePresetStateConfig(
  raw: unknown,
  fallback: PresetSwitchStateConfig,
): PresetSwitchStateConfig {
  if (!isObject(raw)) {
    return { ...fallback };
  }

  return {
    preset: typeof raw.preset === 'string' ? raw.preset : fallback.preset,
    label: typeof raw.label === 'string' ? raw.label : fallback.label,
    icon: typeof raw.icon === 'string' ? raw.icon : fallback.icon,
    classes: typeof raw.classes === 'string' ? raw.classes : '',
    styles: typeof raw.styles === 'string' ? raw.styles : '',
  };
}

export function parsePresetSwitchButtonConfig(
  rawConfig: unknown,
): PresetSwitchButtonConfig {
  if (typeof rawConfig === 'string') {
    const trimmedConfig = rawConfig.trim();

    if (trimmedConfig.startsWith('{')) {
      try {
        return parsePresetSwitchButtonConfig(
          JSON.parse(trimmedConfig) as Record<string, unknown>,
        );
      } catch {
        return defaultPresetSwitchButtonConfig;
      }
    }

    return defaultPresetSwitchButtonConfig;
  }

  if (!isObject(rawConfig)) {
    return defaultPresetSwitchButtonConfig;
  }

  return {
    active:
      typeof rawConfig.active === 'boolean'
        ? rawConfig.active
        : defaultPresetSwitchButtonConfig.active,
    showOnMobile:
      typeof rawConfig.showOnMobile === 'boolean'
        ? rawConfig.showOnMobile
        : defaultPresetSwitchButtonConfig.showOnMobile,
    showOnDesktop:
      typeof rawConfig.showOnDesktop === 'boolean'
        ? rawConfig.showOnDesktop
        : defaultPresetSwitchButtonConfig.showOnDesktop,
    routes:
      Array.isArray(rawConfig.routes) &&
      rawConfig.routes.every((route) => typeof route === 'string')
        ? (rawConfig.routes as string[])
        : [...defaultPresetSwitchButtonConfig.routes],
    tooltipEnabled:
      typeof rawConfig.tooltipEnabled === 'boolean'
        ? rawConfig.tooltipEnabled
        : defaultPresetSwitchButtonConfig.tooltipEnabled,
    primary: parsePresetStateConfig(
      rawConfig.primary,
      defaultPresetSwitchPrimaryState,
    ),
    secondary: parsePresetStateConfig(
      rawConfig.secondary,
      defaultPresetSwitchSecondaryState,
    ),
  };
}

export interface NavigationConfigurations
  extends Configurations<NavigationConfigurationKeys> {
  [NavigationConfigurationKeys.HomeAndBrowseEnabled]: boolean;
  [NavigationConfigurationKeys.OffboardingEnabled]: boolean;
  [NavigationConfigurationKeys.ReportingLinkEnabled]: boolean;
  [NavigationConfigurationKeys.DropshipLinkUrl]: string | DropshipLinkUrlConfig;
  [NavigationConfigurationKeys.PresetSwitchButton]:
    | string
    | PresetSwitchButtonConfig;
}
